import { useState, type ChangeEvent, type FormEvent } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useAnalyticsApi } from '@/lib/useAnalytics';
import { komunikatLogowania } from './komunikatLogowania';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * LOGOWANIE DO PANELU RESTAURACJI — OSOBNY EKRAN, NIE WARIANT LOGOWANIA KONSOLI
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ┌─ CO TU SIĘ WPISUJE ─────────────────────────────────────────────────────────────────────────┐
 * │ LOGIN RESTAURACJI — mówi, CZYJE dane mają się otworzyć.                                     │
 * │ HASŁO             — dowodzi, że wolno je otworzyć.                                          │
 * │                                                                                              │
 * │ Dziś działa tu hasło WŁAŚCICIELA: wskazujesz restaurację jej loginem i potwierdzasz siebie. │
 * │ Hasła restauracji nie znamy, nie sprawdzamy i nie jest do niczego potrzebne — a gdy R5-14   │
 * │ doda wiązanie kont, restauracja zaloguje się tym samym formularzem swoim własnym hasłem.    │
 * └──────────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * ┌─ DLACZEGO NIE EKRAN LOGOWANIA KONSOLI ──────────────────────────────────────────────────────┐
 * │ Tamten opowiada, że Control Center sięga do CRM-u i do systemu kierowców własną tożsamością │
 * │ serwisową. Restauracji nic do tego: opis architektury pokazany najemcy to darmowa mapa      │
 * │ systemu bez żadnej korzyści dla niego.                                                       │
 * └──────────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * Hasło żyje przez czas jednego żądania. Nie trafia do adresu, do pamięci przeglądarki ani do
 * dziennika, a ze stanu komponentu znika natychmiast po wysłaniu — także wtedy, gdy serwer odmówi.
 */
export function LogowanieRestauracji({ onZalogowano }: { onZalogowano?: () => void }) {
  const api = useAnalyticsApi();

  const [login, setLogin] = useState('');
  const [haslo, setHaslo] = useState('');
  const [blad, setBlad] = useState<{ tresc: string; powod: string | null } | null>(null);
  const [pracuje, setPracuje] = useState(false);

  async function wyslij(e: FormEvent) {
    e.preventDefault();
    setBlad(null);
    setPracuje(true);
    try {
      await api.logowanieMistrza(login.trim(), haslo);
      setHaslo('');
      onZalogowano?.();
    } catch (err) {
      // Hasło znika ze stanu NIEZALEŻNIE od wyniku. Odmowa to najgorszy moment na zostawienie
      // go w polu: formularz zostaje otwarty, a hasło leży na ekranie i w pamięci karty.
      setHaslo('');
      setBlad(komunikatLogowania(err));
    } finally {
      setPracuje(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--tlo)',
        p: 2,
      }}
    >
      <Box
        component="form"
        onSubmit={(e: FormEvent) => { void wyslij(e); }}
        sx={{
          width: '100%',
          maxWidth: 420,
          background: 'var(--powierzchnia)',
          border: '1px solid var(--linia)',
          borderRadius: 'var(--r-duzy, 12px)',
          p: { xs: 3, sm: 4 },
        }}
      >
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
            <StorefrontIcon sx={{ color: 'var(--zloto-glebokie)' }} />
            <Typography component="h1" sx={{ fontWeight: 800, fontSize: 22 }}>
              Panel restauracji
            </Typography>
          </Stack>

          <Typography variant="body2" sx={{ color: 'var(--tekst-2)' }}>
            Zamówienia na dziś, karta dań, szkoły, historia i raporty dostaw — dla wskazanej
            restauracji.
          </Typography>

          <TextField
            label="Login restauracji"
            type="email"
            autoComplete="username"
            required
            value={login}
            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setLogin(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            helperText="Dokładny adres, na który restauracja ma konto"
          />
          <TextField
            label="Hasło"
            type="password"
            autoComplete="current-password"
            required
            value={haslo}
            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setHaslo(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />

          {blad && (
            <Alert severity="error">
              {blad.tresc}
              {/* Powód od serwera, gdy go podał — mówi, CO zrobić dalej, a nie tylko że się nie udało. */}
              {blad.powod && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                  {blad.powod}
                </Typography>
              )}
            </Alert>
          )}

          <Button type="submit" variant="contained" size="large" disabled={pracuje}>
            {pracuje ? 'Logowanie…' : 'Zaloguj'}
          </Button>

          <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)' }}>
            Panel pokazuje wyłącznie dane wskazanej restauracji. Zakres ustala serwer przy każdym
            żądaniu — nie da się go zmienić z przeglądarki.
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
}

export default LogowanieRestauracji;
